/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useMemo } from "react";
import { AsYouType } from "libphonenumber-js";

import type { CellPositions, LinkProps, PhoneColumn } from "../types";
import { Cell } from "./cells";
import { useTypo } from "../../typo";
import type { FocusState } from "../use-focus-state";
import { usePinnedTableRow } from "../table";

const formatter = new AsYouType();

export const PhoneCell = <D extends Object>({
  rowIndex,
  colIndex,
  column,
  row,
  focusState,
  onPress,
  linkProps,
  isHovered = false,
  variant = "fill",
  isStickyFirstColumn = false,
  stickyFirstColumnShift = 0,
  stickyRowPositions,
  positions,
  hasSelection,
  selectionOffset,
}: {
  rowIndex: number;
  colIndex: number;
  column: PhoneColumn<D>;
  row: D;
  focusState: FocusState;
  onPress?: () => void;
  linkProps?: LinkProps;
  isHovered?: boolean;
  variant?: "fill" | "ghost";
  isStickyFirstColumn?: boolean;
  stickyFirstColumnShift?: number;
  stickyRowPositions?: { top?: boolean; bottom?: boolean };
  hasSelection: boolean;
  selectionOffset: number;
  positions?: CellPositions;
}) => {
  const typo = useTypo();
  const pinnedTableRow = usePinnedTableRow();
  const value = column.value(row, rowIndex);

  const phone = useMemo(() => {
    if (!value) return undefined;

    formatter.reset();
    formatter.input(value);

    return formatter.getNumber()?.formatInternational();
  }, [value]);

  return (
    <Cell
      isPinnedRow={pinnedTableRow && pinnedTableRow.pinnedRow(row)}
      rowIndex={rowIndex}
      colIndex={colIndex}
      focusState={focusState}
      onPress={onPress}
      linkProps={linkProps}
      isHovered={isHovered}
      variant={variant}
      align="start"
      isStickyFirstColumn={isStickyFirstColumn}
      stickyFirstColumnShift={stickyFirstColumnShift}
      stickyRowPositions={stickyRowPositions}
      positions={positions}
      hasSelection={hasSelection}
      selectionOffset={selectionOffset}
    >
      <span
        dir="ltr"
        css={{
          ...typo({
            level: "body2",
            density: "tight",
            weight: "regular",
            tabularNumbers: true,
          }),
        }}
      >
        {phone}
      </span>
    </Cell>
  );
};
